import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { StatusCodes } from 'http-status-codes'
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { useAuth } from '@/auth/AuthContext'
import { useGetCurrentUserQuery } from '@/features/users/userApi'
import { RouterConfiguration } from '@/routes'

import { AppLayout } from './app-layout'
import { NavPublic } from './nav-public'
import type { IRouteIds, IRouteProps } from './router-types'
import { StaffGuard } from './staff-guard'
import { UnauthorizedView } from './unauthorized'

// Admin routes are code-split: the admin chunk is only fetched when a user
// first navigates to /admin/*, keeping the initial download small for the
// public app.
const AdminRouterLazy = lazy(() =>
  import('@/app/app-router-admin').then(m => ({ default: m.AppRouterAdmin })),
)

const AdminFallback = (): JSX.Element => (
  <div style={{ padding: '2rem' }}>Loading admin&hellip;</div>
)

export type { IRouteIds, IRouteProps }

// Statuses in which the current-user lookup is answering *about this session*: the
// staff realm authenticator rejects a management realm token that lacks `support`
// (401), `IsPlatformUser` rejects anything that is not a platform user (403), and a
// missing record would be a 404. Any other failure — 5xx, offline, a token refresh
// that could not complete — is the lookup itself failing and says nothing about
// permissions.
const NO_PROFILE_STATUSES: ReadonlyArray<number | string> = [
  StatusCodes.UNAUTHORIZED,
  StatusCodes.FORBIDDEN,
  StatusCodes.NOT_FOUND,
]

const isNoProfileAnswer = (
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean => {
  // `/public/v1/users/me/` always carries a user on 200, so a settled query with no
  // error and no data would be the API saying this session has no profile.
  if (error === undefined) return true
  return 'status' in error && NO_PROFILE_STATUSES.includes(error.status)
}

const AppRouter = () => {
  const nav = <NavPublic />
  const routes = RouterConfiguration()
  const { isStaff } = useAuth()
  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    isUninitialized: isCurrentUserUninitialized,
    error: currentUserError,
  } = useGetCurrentUserQuery(undefined, {
    skip: isStaff,
  })
  const userId = currentUser?.id
  // While the query is in flight `currentUser` is undefined too, so the two states have to
  // be told apart: only a settled lookup can say this session has nowhere to go — and only
  // when what came back was an answer about the session rather than a failed call.
  const isResolvingCurrentUser =
    isCurrentUserLoading || isCurrentUserUninitialized
  const hasNoProfile =
    !isResolvingCurrentUser && isNoProfileAnswer(currentUserError)

  return (
    <Routes>
      <Route
        path='/'
        element={
          userId !== undefined ? (
            <Navigate to={`/user/details/${userId}`} replace />
          ) : isStaff ? (
            <Navigate to='/admin/accounts' replace />
          ) : hasNoProfile ? (
            <UnauthorizedView />
          ) : null
        }
      />
      {routes.map(route => (
        <Route
          key={route.id || route.path}
          path={route.path}
          element={
            route.requiresStaff ? (
              <StaffGuard>
                <AppLayout
                  documentTitle={route.documentTitle}
                  nav={nav}
                  view={route.view}
                />
              </StaffGuard>
            ) : (
              <AppLayout
                documentTitle={route.documentTitle}
                nav={nav}
                view={route.view}
              />
            )
          }
        />
      ))}
      <Route
        path='/admin/*'
        element={
          <Suspense fallback={<AdminFallback />}>
            <AdminRouterLazy />
          </Suspense>
        }
      />
    </Routes>
  )
}

export { AppRouter }
